import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { getReportsFindAllQueryKey } from '@/api/WhisApi/generated';
import { showToast } from '@/components/Toast/toast.service';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import {
  readReportSubmissionQueue,
  writeReportSubmissionQueue,
} from './offlineReportSubmissionQueue.storage';
import { processQueuedSubmission } from './offlineReportSubmissionQueue.submission';
import type {
  EnqueueReportSubmissionInput,
  QueuedReportSubmission,
} from './offlineReportSubmissionQueue.types';
import {
  buildQueuedSubmission,
  shouldStopQueueProcessing,
} from './offlineReportSubmissionQueue.utils';

export {
  isOfflineSubmissionError,
  isUnauthorizedSubmissionError,
} from './offlineReportSubmissionQueue.submission';

interface UseOfflineReportSubmissionQueueResult {
  enqueueReportSubmission: (
    input: EnqueueReportSubmissionInput,
  ) => Promise<void>;
  processReportSubmissionQueue: () => Promise<void>;
  isQueueProcessing: boolean;
}

export const useOfflineReportSubmissionQueue =
  (): UseOfflineReportSubmissionQueueResult => {
    const { t } = useAppTranslation();
    const queryClient = useQueryClient();
    // Prevent running queue processing multiple times in parallel.
    const isProcessingRef = useRef(false);

    const processQueueMutation = useMutation({
      mutationKey: ['offlineReportSubmissionQueue', 'process'],
      mutationFn: async (): Promise<boolean> => {
        if (isProcessingRef.current) {
          return false;
        }

        isProcessingRef.current = true;

        try {
          const queuedSubmissions = await readReportSubmissionQueue();

          if (!queuedSubmissions.length) {
            return false;
          }

          const remainingSubmissions: QueuedReportSubmission[] = [];
          let hasProcessedAtLeastOne = false;

          // Process items in background sequentially to preserve order.
          for (const [index, queuedSubmission] of queuedSubmissions.entries()) {
            try {
              await processQueuedSubmission(queuedSubmission);
              hasProcessedAtLeastOne = true;
            } catch (error) {
              remainingSubmissions.push(queuedSubmission);

              // Stop on blocking errors and keep remaining items for later.
              if (shouldStopQueueProcessing(error)) {
                remainingSubmissions.push(
                  ...queuedSubmissions.slice(index + 1),
                );
                break;
              }
            }
          }

          await writeReportSubmissionQueue(remainingSubmissions);

          if (hasProcessedAtLeastOne) {
            await queryClient.invalidateQueries({
              queryKey: getReportsFindAllQueryKey(),
            });
          }

          return hasProcessedAtLeastOne;
        } finally {
          isProcessingRef.current = false;
        }
      },
      retry: false,
    });

    const enqueueReportSubmission = async (
      input: EnqueueReportSubmissionInput,
    ): Promise<void> => {
      const queuedSubmissions = await readReportSubmissionQueue();
      await writeReportSubmissionQueue([
        ...queuedSubmissions,
        buildQueuedSubmission(input),
      ]);
    };

    const processReportSubmissionQueue = async (): Promise<void> => {
      const hasProcessedAtLeastOne = await processQueueMutation.mutateAsync();

      if (hasProcessedAtLeastOne) {
        showToast({
          type: 'success',
          title: t('reportCreation.toasts.offlineQueueProcessed'),
        });
      }
    };

    return {
      enqueueReportSubmission,
      processReportSubmissionQueue,
      isQueueProcessing: processQueueMutation.isPending,
    };
  };
